<script setup>
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

const { t } = useI18n()
const authStore = useAuthStore()

const isOpen = ref(false)
const menu = ref()

const isAppAdmin = computed(() =>
  authStore.userRoles.includes('app-administrator'),
)

const isClientAdmin = computed(() =>
  authStore.userRoles.includes('client-administrator'),
)

const isSystemAdmin = computed(() =>
  authStore.userRoles.includes('system-administrator'),
)

const settingsLinks = computed(() => {
  const links = []

  if (isAppAdmin.value || isSystemAdmin.value) {
    links.push(
      { label: t('core.menu.general.platform-management'), to: '/platform-admin' },
    )
  }

  if (isClientAdmin.value) {
    links.push({
      label: t('core.menu.general.tenant-admin'),
      to: `/tenant-admin/${authStore.tenantData?.economicGroup}`,
      showDivider: isAppAdmin.value || isSystemAdmin.value,
    })
  }

  return links
})

const hasMenuAccess = computed(() => isAppAdmin.value || isClientAdmin.value || isSystemAdmin.value)

function displayContextMenu(event) {
  menu.value.toggle(event)
}

function popoverShow() {
  isOpen.value = true
}

function popoverHide() {
  isOpen.value = false
}

function handleMenuClick() {
  menu.value?.hide()
}

const handleScroll = () => menu.value?.hide()

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div
    v-if="hasMenuAccess"
    class="relative flex items-center justify-center"
  >
    <Button
      v-tooltip.bottom="t('core.header.tooltips.settings')"
      icon="pi pi-cog"
      class="w-11! h-11! p-0! bg-dss-surface-200 hover:bg-dss-surface-250 transition-colors duration-200 rounded-2xl border-0 focus:outline-none focus:ring-0 transform-gpu will-change-transform text-dss-secondary-500"
      :class="{ 'bg-dss-surface-100': isOpen }"
      :pt="{ label: { class: 'hidden' } }"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="settings-popover"
      @click="displayContextMenu($event)"
    />

    <Popover
      id="settings-popover"
      ref="menu"
      append-to="self"
      class="bg-white before:hidden after:hidden rounded-2xl! border! border-dss-surface-200 shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A] left-auto! right-0! top-9!"
      :pt="{ content: { class: 'p-2' } }"
      @show="popoverShow"
      @hide="popoverHide"
    >
      <div class="flex flex-col gap-4 py-2 px-3">
        <span class="block text-dss-surface-850 text-xs font-medium uppercase">{{ t('core.menu.general.settings') }}</span>

        <div class="flex flex-col gap-2">
          <template
            v-for="link in settingsLinks"
            :key="link.to"
          >
            <Divider
              v-if="link.showDivider"
              class="-mx-3! my-2! w-auto!"
            />

            <router-link
              :to="link.to"
              class="text-dss-surface-950 text-sm font-medium hover:underline whitespace-nowrap"
              @click="handleMenuClick"
            >
              {{ link.label }}
            </router-link>
          </template>
        </div>
      </div>
    </Popover>
  </div>
</template>
